import React, { useContext, ContextType, useState } from "react";
import { Context } from "..";
import { MdDelete, MdEdit } from "react-icons/md";
import BudgetFormModal from "./BudgetFormModal";
import Modal from "./Modal";
import { MonthsAbr } from "./dateConstants";
import Table from "./Table";
import { useTranslation } from "react-i18next";

type BudgetProps = {
  budget: ContextType<typeof Context>["budgets"][0][0];
  monthDate?: Date;
};

export const Budget: React.FC<BudgetProps> = ({
  budget,
  monthDate = new Date(),
}) => {
  const { transactions, events, budgets, expenseCategories } = useContext(
    Context
  );
  const [modalOpened, setModalOpened] = useState(false);
  const [transactionsModalOpened, setTransactionsModalOpened] = useState(false);
  const startOfTheMonth = new Date(monthDate);
  const endOfTheMonth = new Date(
    monthDate.getFullYear(),
    monthDate.getMonth() + 1,
    0,
    23,
    59,
    59
  );
  const startOfTheYear = new Date(monthDate.getFullYear(), 0);
  const endOfTheYear = new Date(monthDate.getFullYear() + 1, 0, -1, 23, 59, 59);
  const { t } = useTranslation();

  const { span, value, containing } = budget;

  let filteredTransactions = [...transactions[0]];

  // filter only expenses
  filteredTransactions = filteredTransactions.filter((t) => t.value < 0);

  // filter by attached category/event
  filteredTransactions =
    budget.attachedTo.id === "*"
      ? filteredTransactions
      : filteredTransactions.filter(
          (t) => t.attachedTo.id === budget.attachedTo.id
        );

  // filter by phrase
  if (containing) {
    filteredTransactions = filteredTransactions.filter((ft) =>
      ft.description?.includes(containing)
    );
  }

  // filter by date
  filteredTransactions =
    span === "MONTHLY"
      ? filteredTransactions.filter(
          (t) => t.date > startOfTheMonth && t.date < endOfTheYear
        )
      : filteredTransactions.filter(
          (t) => t.date > startOfTheYear && t.date < endOfTheMonth
        );

  const expenseSum = filteredTransactions.reduce(
    (acc, val) => acc + Math.abs(val.value),
    0
  );

  const widthPercent = Math.min(100, (expenseSum * 100) / value);

  const deleteBudget = () => {
    budgets[1]((budgets) => budgets.filter((b) => b.id !== budget.id));
  };

  return (
    <div className="shadow-md hover:shadow-xl bg-gray-50 rounded-xl">
      {modalOpened && (
        <BudgetFormModal
          onClose={() => setModalOpened(false)}
          updateObj={budget}
        />
      )}
      {transactionsModalOpened && (
        <Modal onClose={() => setTransactionsModalOpened(false)}>
          <div className="p-10">
            <h1 className="mb-10 text-2xl">
              {budget.span === "MONTHLY"
                ? `Transactions of this budget for month ${
                    MonthsAbr[monthDate.getMonth()]
                  } ${monthDate.getFullYear()}`
                : `Transactions of this budget for year ${monthDate.getFullYear()}`}
            </h1>
            <Table transactions={filteredTransactions} />
          </div>
        </Modal>
      )}
      <div className="p-5 pb-8">
        <h2 className="text-2xl text-gray-600 border-none w-full resize-none">
          {budget.name}
        </h2>
        <div className="text-center font-bold mt-3">
          <span
            onClick={
              filteredTransactions.length > 0
                ? () => setTransactionsModalOpened(true)
                : undefined
            }
            className={
              filteredTransactions.length > 0 ? "underline cursor-pointer" : ""
            }
          >
            {expenseSum}€
          </span>{" "}
          / {value}€
        </div>
        <div className="rounded-full relative h-5 w-full border-gray-300 border my-2">
          <div
            style={{ width: `${widthPercent}%` }}
            className={`absolute h-full left-0 top-0 rounded-full ${
              expenseSum > value ? "bg-red-500" : "bg-green-500"
            }`}
          ></div>
        </div>

        <div className="flex">
          <div className="flex-grow text-gray-500">
            {budget.attachedTo.type === "EVENT" ? t("Event") : t("Category")}
          </div>
          <div className="text-sm font-bold">
            {(() => {
              if (budget.attachedTo.type === "CATEGORY") {
                if (budget.attachedTo.id === "*") {
                  return t("All categories");
                } else {
                  return (
                    expenseCategories[0].find(
                      (e) => e.id === budget.attachedTo.id
                    )?.label || "-"
                  );
                }
              } else {
                return (
                  events[0].find((e) => e.id === budget.attachedTo.id)?.name ||
                  "-"
                );
              }
            })()}
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex-grow text-gray-500">{t("Frequency")}</div>
          <div className="text-sm font-bold">
            {budget.attachedTo.type === "EVENT"
              ? events[0].find((e) => e.id === budget.attachedTo.id)?.frequency
              : budget.span === "MONTHLY"
              ? t("Monthly")
              : t("Annual")}
          </div>
        </div>
        {budget.containing && (
          <div className="flex items-center">
            <div className="flex-grow text-gray-500">{t("Must include")}</div>
            <div className="text-sm font-bold">{budget.containing}</div>
          </div>
        )}
      </div>
      <div className="border-t border-gray-300 pt-4 p-5 flex">
        <div className="flex-grow">
          <button
            onClick={() => setModalOpened(true)}
            className="outline-none focus:outline-none px-4 py-1 flex items-center border border-gray-300 rounded-full hover:bg-green-700 hover:text-white transition-colors duration-300"
          >
            <MdEdit className="cursor-pointer mr-1" size={15} /> {t("Update")}
          </button>
        </div>
        <button
          onClick={deleteBudget}
          className="outline-none focus:outline-none px-4 py-1 flex items-center border border-gray-300 rounded-full hover:bg-red-400 hover:text-white transition-colors duration-300"
        >
          <MdDelete className="text-red-500 cursor-pointer mr-1" size={15} />{" "}
          {t("Delete")}
        </button>
      </div>
    </div>
  );
};
